import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  FileUploadStatus,
  UploadState,
} from '../doqfy-components/bulk-upload-pdf-estamp/bulk-upload.interfaces';

@Injectable({
  providedIn: 'root',
})
export class UploadProgressService {
  private uploadStateSubject = new BehaviorSubject<UploadState>({
    isActive: false,
    isMinimized: false,
    files: [],
    totalFiles: 0,
    successCount: 0,
    errorCount: 0,
    pendingCount: 0,
    currentFileIndex: -1,
  });

  public uploadState$: Observable<UploadState> =
    this.uploadStateSubject.asObservable();

  constructor() {}

  startUpload(files: FileUploadStatus[]) {
    const state = this.uploadStateSubject.value;
    this.uploadStateSubject.next({
      ...state,
      isActive: true,
      isMinimized: false,
      files: files,
      totalFiles: files.length,
      successCount: 0,
      errorCount: 0,
      pendingCount: files.length,
      currentFileIndex: 0,
    });
  }

  updateFileStatus(index: number, updates: Partial<FileUploadStatus>) {
    const state = this.uploadStateSubject.value;
    const updatedFiles = [...state.files];
    updatedFiles[index] = { ...updatedFiles[index], ...updates };

    const successCount = updatedFiles.filter(
      (f) => f.status === 'success',
    ).length;
    const errorCount = updatedFiles.filter((f) => f.status === 'error').length;
    const pendingCount = updatedFiles.filter(
      (f) => f.status === 'pending',
    ).length;

    this.uploadStateSubject.next({
      ...state,
      files: updatedFiles,
      successCount,
      errorCount,
      pendingCount,
    });
  }

  setCurrentFileIndex(index: number) {
    const state = this.uploadStateSubject.value;
    this.uploadStateSubject.next({
      ...state,
      currentFileIndex: index,
    });
  }

  toggleMinimize() {
    const state = this.uploadStateSubject.value;
    this.uploadStateSubject.next({
      ...state,
      isMinimized: !state.isMinimized,
    });
  }

  setMinimized(minimized: boolean) {
    const state = this.uploadStateSubject.value;
    this.uploadStateSubject.next({
      ...state,
      isMinimized: minimized,
    });
  }

  completeUpload() {
    const state = this.uploadStateSubject.value;
    this.uploadStateSubject.next({
      ...state,
      isActive: false,
      currentFileIndex: -1,
    });
  }

  resetUpload() {
    this.uploadStateSubject.next({
      isActive: false,
      isMinimized: false,
      files: [],
      totalFiles: 0,
      successCount: 0,
      errorCount: 0,
      pendingCount: 0,
      currentFileIndex: -1,
    });
  }

  retryFile(index: number) {
    // Emit retry event - components should subscribe to uploadState$ to detect changes
    const state = this.uploadStateSubject.value;
    const updatedFiles = [...state.files];
    if (updatedFiles[index]) {
      updatedFiles[index] = {
        ...updatedFiles[index],
        status: 'pending',
        progress: 0,
        errorMessage: undefined,
      };

      this.uploadStateSubject.next({
        ...state,
        files: updatedFiles,
      });
    }
  }

  getUploadState(): UploadState {
    return this.uploadStateSubject.value;
  }

  isUploadActive(): boolean {
    return this.uploadStateSubject.value.isActive;
  }
}
